import { ArpMediaType, User } from '@arpeggi/shared'
import { SnapshotIn, types } from 'mobx-state-tree'
import { ForDirectExtend } from '../../../features/mobx'
import { getUserFavorites } from '../../api/v2/ArpFavoritesAPI'
import axios from '../../lib/axios'
import { createRegistry } from './createRegistry'

const _Profile = types
  .model({
    id: types.maybe(types.number),
    wallet_address: types.identifier,
    display_name: types.maybeNull(types.string),
    profile_picture: types.maybeNull(types.string),
    bio: types.maybeNull(types.string),
    favorite_genesis_songs: types.array(types.union(types.number, types.string)),
    favorite_songs: types.array(types.union(types.number, types.string)),
    favorite_stems: types.array(types.union(types.number, types.string)),
    favorite_samples: types.array(types.union(types.number, types.string)),
    twitter_handle: types.maybeNull(types.string),
    opensea_handle: types.maybeNull(types.string),
    soundcloud_handle: types.maybeNull(types.string),
    type: types.enumeration(['normal', 'admin', 'god'])
  })
  .actions((self) => {
    async function afterCreate() {
      await Promise.all([
        _fetchFavoriteStuff(ArpMediaType.STEM, 'favorite_stems'),
        _fetchFavoriteStuff(ArpMediaType.SONG, 'favorite_songs'),
        _fetchFavoriteStuff(ArpMediaType.SAMPLE, 'favorite_samples'),
        _fetchFavoriteGenesis()
      ])
    }

    async function _fetchFavoriteStuff(
      type: ArpMediaType,
      key: 'favorite_stems' | 'favorite_samples' | 'favorite_songs'
    ) {
      const { data } = await getUserFavorites(self.wallet_address, { type, perPage: 10000 })

      const indexes = data.map((media) => media.id)
      self[key].clear()
      self[key].push(...indexes)
    }

    async function _fetchFavoriteGenesis() {
      const { data } = await axios.get(`/api/v1/wallet/${self.wallet_address}/genesis-favorites`)
      const indexes = data.data.map((media) => media.song)
      self['favorite_genesis_songs'].clear()
      self['favorite_genesis_songs'].push(...indexes)
    }

    return {
      afterCreate
    }
  })
  .preProcessSnapshot((snapshot: User): SnapshotIn<typeof _Profile> => {
    if (!snapshot) return null
    const {
      walletAddress,
      displayName,
      profilePicture,
      openSeaHandle,
      soundCloudHandle,
      twitterHandle,
      bio,
      id
    } = snapshot
    // include profile favorites e.t.c.
    return {
      id: id,
      wallet_address: walletAddress,
      display_name: displayName,
      profile_picture: profilePicture?.url,
      opensea_handle: openSeaHandle,
      soundcloud_handle: soundCloudHandle,
      twitter_handle: twitterHandle,
      bio,
      ...snapshot
    }
  })

interface ProfileType extends ForDirectExtend<typeof _Profile> {}
export const Profile: ProfileType = _Profile

const ProfileRegistry = createRegistry(Profile, {
  baseURL: '/api/v2/users',
  primaryKey: 'wallet_address'
}).create({ status: 'ready' })

export interface IProfile extends SnapshotIn<typeof Profile> {}

export default ProfileRegistry
